import { useEffect } from "react";
import { useAuthStore, type AuthStatus } from "../../stores/auth-store";
import type {
  AuthError,
  AuthSessionRecord,
  AuthUser,
  EmailOtpVerifyResult,
  OnboardingState,
} from "./types";

export interface UseAuthResult {
  status: AuthStatus;
  user: AuthUser | null;
  onboarding: OnboardingState | null;
  error: AuthError | null;

  isLoading: boolean;
  isAuthenticated: boolean;
  /** Signed in, but the onboarding answers are not finished. */
  needsOnboarding: boolean;
  /** True only when the last verify created the account. A routing hint, not a gate. */
  isNewUser: boolean;
  /**
   * True when the last failure was a request that never got a server verdict.
   * Show a retry, not a sign in wall.
   */
  isOffline: boolean;

  refresh: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  requestEmailOtp: (email: string) => Promise<void>;
  verifyEmailOtp: (email: string, code: string) => Promise<EmailOtpVerifyResult>;
  updateOnboarding: (partial: Partial<OnboardingState>) => Promise<OnboardingState>;
  signOut: () => Promise<void>;

  /** null until loadSessions has run, which the Account panel does on mount. */
  sessions: AuthSessionRecord[] | null;
  sessionsLoading: boolean;
  loadSessions: () => Promise<void>;

  updateProfile: (partial: {
    firstName?: string | null;
    lastName?: string | null;
  }) => Promise<AuthUser>;
  removeAvatar: () => Promise<AuthUser>;
  revokeSession: (sessionId: string) => Promise<void>;
  revokeAllSessions: () => Promise<void>;
  deleteAccount: (confirmEmail: string) => Promise<void>;
}

/**
 * Session state for components.
 *
 * Mounting this hook is what starts hydration, so a component tree does not
 * need an explicit bootstrap call. The store deduplicates, so ten components
 * calling useAuth() still means one /auth/me.
 *
 * Fields are selected one at a time on purpose: each selector returns either a
 * primitive or a reference the store keeps stable, so components re-render on
 * real changes rather than on every store write.
 */
export function useAuth(): UseAuthResult {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const onboarding = useAuthStore((s) => s.onboarding);
  const error = useAuthStore((s) => s.error);
  const isNewUser = useAuthStore((s) => s.isNewUser);

  const hydrate = useAuthStore((s) => s.hydrate);
  const refresh = useAuthStore((s) => s.refresh);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const sendMagicLink = useAuthStore((s) => s.sendMagicLink);
  const requestEmailOtp = useAuthStore((s) => s.requestEmailOtp);
  const verifyEmailOtp = useAuthStore((s) => s.verifyEmailOtp);
  const updateOnboarding = useAuthStore((s) => s.updateOnboarding);
  const signOut = useAuthStore((s) => s.signOut);

  const sessions = useAuthStore((s) => s.sessions);
  const sessionsLoading = useAuthStore((s) => s.sessionsLoading);
  const loadSessions = useAuthStore((s) => s.loadSessions);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const removeAvatar = useAuthStore((s) => s.removeAvatar);
  const revokeSession = useAuthStore((s) => s.revokeSession);
  const revokeAllSessions = useAuthStore((s) => s.revokeAllSessions);
  const deleteAccount = useAuthStore((s) => s.deleteAccount);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  return {
    status,
    user,
    onboarding,
    error,
    isLoading: status === "loading",
    isAuthenticated: status === "authed",
    needsOnboarding: status === "authed" && onboarding !== null && !onboarding.completed,
    isNewUser,
    isOffline: error !== null && error.kind === "network",
    refresh,
    signInWithGoogle,
    sendMagicLink,
    requestEmailOtp,
    verifyEmailOtp,
    updateOnboarding,
    signOut,
    sessions,
    sessionsLoading,
    loadSessions,
    updateProfile,
    removeAvatar,
    revokeSession,
    revokeAllSessions,
    deleteAccount,
  };
}
